import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';
import { IService } from '@clickersphere/core/entities/IService';
import { ResponseError } from '@clickersphere/core/entities/ResponseError';
import { useCreateService } from '../../../../app/hooks/service/useCreateService';
import { useUpdateService } from '../../../../app/hooks/service/useUpdateService';
import { useError } from '../../../../app/hooks/useError';
import useToast from '../../../../app/hooks/useToast';
import { useStore } from '../../../../app/store';

interface IUseRegisterServiceFormController {
  service?: IService;
}

type FormData = {
  name: string;
  description?: string;
  price: number;
  imageUrl?: string | null;
};

export function useRegisterServiceFormController({
  service,
}: IUseRegisterServiceFormController) {
  const { t } = useTranslation();
  const { handleCloseModal } = useStore(
    useShallow((state) => ({
      handleCloseModal: state.modal.handleCloseModal,
    })),
  );
  const { toast } = useToast();
  const { handleError } = useError();
  const { createServiceAsync, createServiceIsPending } = useCreateService();
  const { updateServiceAsync, updateServiceIsPending } = useUpdateService();

  const schema = z.object({
    name: z
      .string()
      .min(1, t('catalog.form.errors.nameRequired'))
      .max(100, t('catalog.form.errors.nameMax')),
    description: z
      .string()
      .max(500, t('catalog.form.errors.descriptionMax'))
      .optional()
      .transform((v) => (v === '' ? undefined : v)),
    price: z
      .number({ message: t('catalog.form.errors.pricePositive') })
      .positive(t('catalog.form.errors.pricePositive')),
    imageUrl: z.union([z.string(), z.null()]).optional(),
  });

  const {
    handleSubmit: hookFormHandleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: service
      ? {
          name: service.name,
          description: service.description ?? '',
          price: service.price / 100,
          imageUrl: undefined,
        }
      : {
          name: '',
          description: '',
          price: 0,
          imageUrl: undefined,
        },
  });

  const handleSubmit = hookFormHandleSubmit(async (data) => {
    const imageUrlPayload =
      data.imageUrl === null
        ? null
        : data.imageUrl && data.imageUrl !== ''
          ? data.imageUrl
          : undefined;

    try {
      if (!service) {
        const createPayload = {
          name: data.name,
          description: data.description,
          price: Math.round(data.price * 100),
          ...(typeof imageUrlPayload === 'string'
            ? { imageUrl: imageUrlPayload }
            : {}),
        };
        await createServiceAsync(createPayload);
      } else {
        const updatePayload: {
          name: string;
          description?: string;
          price: number;
          imageUrl?: string | null;
        } = {
          name: data.name,
          description: data.description,
          price: Math.round(data.price * 100),
        };
        if (imageUrlPayload !== undefined) {
          updatePayload.imageUrl = imageUrlPayload;
        }
        await updateServiceAsync({ id: service.id, data: updatePayload });
      }
      handleCloseModal();
    } catch (error) {
      if (error instanceof ResponseError) {
        handleError(error);
      } else {
        toast(t('catalog.form.actions.saveError'), 'error');
      }
    }
  });

  return {
    isFetching: createServiceIsPending || updateServiceIsPending,
    errors,
    control,
    register,
    handleSubmit,
    handleFormReset: reset,
  };
}
